/** 변환 결과 리뷰 패널 — 출고전표 후보 리스트 + 미매핑 품목/단가 인라인 등록 UI. */
import { Fragment, useEffect, useState } from 'react'
import { Loader2, PackagePlus, RotateCcw, Save } from 'lucide-react'

import { api } from '@/api/client'
import { useToast } from '@/stores/toast'
import type { Partner, PartnerPrice } from '@/types/models'
import { Badge } from '@/components/Badge'
import { cn } from '@/lib/utils'
import { MissingItemsMapper } from './MissingItemsMapper'
import { MissingPricesMapper } from './MissingPricesMapper'
import type {
  CommitResult, ItemRow, OrderRow, PartnerRow, PriceRow, UnmatchedDept,
} from './types'

interface ReviewProps {
  committed: CommitResult | null
  committing: boolean
  partnerRows: PartnerRow[]; setPartnerRows: React.Dispatch<React.SetStateAction<PartnerRow[]>>
  onRenamePartner: (oldName: string, newName: string) => void
  onRenameItem: (partnerName: string, oldItemName: string, newItemName: string) => void
  onChangeItemUnit: (partnerName: string, itemName: string, newUnit: string) => void
  itemRows: ItemRow[]; setItemRows: React.Dispatch<React.SetStateAction<ItemRow[]>>
  itemGroups: Array<[string, { is_new: boolean; rows: Array<{ row: ItemRow; idx: number }> }]>
  priceRows: PriceRow[]; setPriceRows: React.Dispatch<React.SetStateAction<PriceRow[]>>
  orderRows: OrderRow[]; setOrderRows: React.Dispatch<React.SetStateAction<OrderRow[]>>
  itemsMatched: number; pricesMatched: number
  onCommit: () => void
  groupId: number | null
  groupName: string | null
  unmatchedDepts: UnmatchedDept[]
  /** 부서 매핑 등록 후 자동 재변환. 없으면 안내만. */
  onRerunConvert?: () => void
}

export function ReviewPanel(p: ReviewProps) {
  const totalNew = p.partnerRows.length + p.itemRows.length + p.priceRows.length + p.orderRows.length
  // 매칭 결과가 하나도 없더라도 unmatched_depts 가 있으면 사용자가 원인을 알 수 있게 표시.
  if (totalNew === 0 && p.itemsMatched === 0 && p.pricesMatched === 0 && !p.committed && p.unmatchedDepts.length === 0) return null

  if (p.committed) return <CommitResultBanner committed={p.committed} />

  // 출고전표 후보 0건 + 미매칭 부서 있음 → 라우팅 실패 케이스 (매핑 등록 안내)
  if (p.orderRows.length === 0 && p.unmatchedDepts.length > 0) {
    return <RoutingFailureBanner groupId={p.groupId} groupName={p.groupName} depts={p.unmatchedDepts} onRerunConvert={p.onRerunConvert}/>
  }

  const selectedOrders = p.orderRows.filter((r) => r.include).length
  const totalSelected = selectedOrders

  // 미리보기용 — 선택된 주문의 미등록 항목 리스트업
  const includedOrders = p.orderRows.filter((r) => r.include)
  const missingItems: Set<string> = new Set()
  // item_name → PO 원본 코드 (가장 처음 발견된 값 유지)
  const missingItemsCode: Record<string, string> = {}
  interface MissingPriceEntry {
    partner: string
    partner_id: number | null
    item: string
    item_id: number | null
    matched_item_name: string
    dates: Set<string>
    po_price: string
  }
  const missingPrices: Map<string, MissingPriceEntry> = new Map()
  for (const r of includedOrders) {
    for (const l of r.lines) {
      if (l.is_new_item) {
        missingItems.add(l.item_name)
        if (l.source_code && !missingItemsCode[l.item_name]) {
          missingItemsCode[l.item_name] = l.source_code
        }
      } else if (l.db_price_missing) {
        const k = `${r.partner_name}||${l.item_name}`
        if (!missingPrices.has(k)) {
          missingPrices.set(k, {
            partner: r.partner_name,
            partner_id: r.partner_id ?? null,
            item: l.item_name,
            item_id: l.matched_item_id ?? null,
            matched_item_name: l.matched_item_name ?? l.item_name,
            dates: new Set(),
            po_price: l.unit_price ?? '0',
          })
        }
        missingPrices.get(k)!.dates.add(r.order_date)
      }
    }
  }
  const missingItemsList = Array.from(missingItems).sort()
  const missingPricesList = Array.from(missingPrices.values())
    .map((v) => ({ ...v, dates: Array.from(v.dates).sort() }))
    .sort((a, b) => a.partner.localeCompare(b.partner) || a.item.localeCompare(b.item))

  return (
    <div className="rounded-xl bg-blue-50/60 border border-blue-200 mb-4 overflow-hidden">
      <div className="px-4 py-3 bg-blue-50 border-b border-blue-200/70">
        <p className="font-semibold text-blue-900 text-sm">
          출고전표 후보 {p.orderRows.length}건
          {p.itemsMatched > 0 && (
            <span className="ml-2 text-xs font-normal text-blue-700/70">
              (품목 매칭: {p.itemsMatched}, 단가 매칭: {p.pricesMatched})
            </span>
          )}
        </p>
        <p className="text-xs text-blue-700/70 mt-0.5">
          이미 등록된 품목 + 거래처별 단가가 있는 라인만 저장됩니다.
          미등록은 아래 알림 리스트를 확인해서 <b>품목관리 / 거래처별 단가에 수동 등록</b>하세요.
        </p>
        {(missingItemsList.length > 0 || missingPricesList.length > 0) && (
          <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
            {missingItemsList.length > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-rose-100 text-rose-800 font-semibold">
                품목 미등록 {missingItemsList.length}종
              </span>
            )}
            {missingPricesList.length > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-amber-100 text-amber-800 font-semibold">
                단가 미등록 {missingPricesList.length}조합
              </span>
            )}
            <span className="text-slate-500">→ 등록하면 저장 안 됨. 아래 리스트 확인 후 수동 등록 필요</span>
          </div>
        )}
      </div>

      {p.unmatchedDepts.length > 0 && <UnmatchedDeptsAlert depts={p.unmatchedDepts}/>}

      {missingItemsList.length > 0 && (
        <MissingItemsMapper
          items={missingItemsList}
          itemCodes={missingItemsCode}
          groupId={p.groupId}
          groupName={p.groupName}
          onMapped={async (itemName, matchedItem) => {
            // 매핑한 품목의 단가 목록 조회 (그룹 스코프)
            let priceDates: string[] = []
            if (p.groupId) {
              try {
                const prices = await api<PartnerPrice[]>(
                  `/api/prices?item=${matchedItem.id}&group=${p.groupId}`,
                )
                priceDates = prices.map((x) => x.effective_from).sort()
              } catch { /* 조회 실패 시 db_price_missing 유지 */ }
            }
            const hasPriceFor = (orderDate: string) =>
              priceDates.some((d) => d <= orderDate)

            p.setOrderRows((cur) => cur.map((order) => {
              const changed = order.lines.some((l) => l.item_name === itemName && l.is_new_item)
              if (!changed) return order
              const hasPrice = hasPriceFor(order.order_date)
              const nextLines = order.lines.map((l) =>
                l.item_name === itemName
                  ? {
                      ...l,
                      is_new_item: false,
                      matched_item_id: matchedItem.id,
                      matched_item_name: matchedItem.name,
                      // 매핑한 품목에 단가 있으면 미등록 표시 해제
                      db_price_missing: !hasPrice,
                    }
                  : l,
              )
              return {
                ...order,
                lines: nextLines,
                new_item_count: nextLines.filter((l) => l.is_new_item).length,
                new_item_names: order.new_item_names?.filter((n) => n !== itemName),
                missing_price_count: nextLines.filter((l) => l.db_price_missing).length,
              }
            }))
          }}
        />
      )}

      {missingPricesList.length > 0 && (
        <MissingPricesMapper
          entries={missingPricesList}
          groupId={p.groupId}
          onSaved={(_partnerName, itemName) => {
            // 상위 스코프로 저장했으므로 모든 파트너의 같은 품목 라인 갱신.
            p.setOrderRows((cur) => cur.map((order) => {
              const changed = order.lines.some((l) => l.item_name === itemName && l.db_price_missing)
              if (!changed) return order
              const nextLines = order.lines.map((l) =>
                l.item_name === itemName ? { ...l, db_price_missing: false } : l,
              )
              return {
                ...order,
                lines: nextLines,
                missing_price_count: nextLines.filter((l) => l.db_price_missing).length,
              }
            }))
          }}
        />
      )}

      <OrderSection p={p}/>

      <div className="px-4 py-3 bg-white/60 border-t border-blue-200/70 flex items-center gap-3">
        <span className="text-xs text-slate-500 tabular-nums">
          선택된 출고전표: {selectedOrders}건
        </span>
        <div className="flex-1"/>
        <button onClick={p.onCommit} disabled={p.committing || totalSelected === 0}
                className="bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 text-white text-sm font-semibold rounded-lg px-4 py-2 inline-flex items-center gap-2">
          {p.committing ? <Loader2 className="animate-spin h-4 w-4"/> : <PackagePlus className="w-4 h-4"/>}
          확인 추가 ({totalSelected}건)
        </button>
      </div>
    </div>
  )
}

// ─── 커밋 성공 배너 ───────────────────────────────────────────────

function CommitResultBanner({ committed }: { committed: CommitResult }) {
  const c = committed.created
  const s = committed.skipped
  const mn = committed.manual_needed
  return (
    <div className="rounded-xl bg-emerald-50 border border-emerald-200 mb-4 px-4 py-4 text-sm text-emerald-900">
      <div className="font-semibold text-base mb-1">✓ 출고전표 {c.orders}건 등록</div>
      {(s.lines_no_item > 0 || s.lines_no_price > 0 || s.orders > 0) && (
        <div className="text-xs text-emerald-700/80 mt-2">
          건너뜀: 라인 {s.lines_no_item + s.lines_no_price}건
          ({s.lines_no_item > 0 && `품목 미등록 ${s.lines_no_item}`}
          {s.lines_no_item > 0 && s.lines_no_price > 0 && ', '}
          {s.lines_no_price > 0 && `단가 미등록 ${s.lines_no_price}`})
          {s.orders > 0 && ` · 주문 ${s.orders}건 (모든 라인 skip)`}
        </div>
      )}
      {committed.orders.length > 0 && (
        <div className="text-xs text-emerald-700/80 mt-1.5">
          출고전표: {committed.orders.map((o) => `${o.order_no} (${o.lines}건/${Number(o.total).toLocaleString()}원)`).join(', ')}
        </div>
      )}
      {(mn.items.length > 0 || mn.prices.length > 0) && (
        <div className="mt-3 rounded-lg bg-white border border-rose-300 p-3 text-slate-800">
          <div className="text-sm font-semibold text-rose-800 mb-2">⚠ 수동 등록 필요</div>
          {mn.items.length > 0 && (
            <div className="mb-2">
              <div className="text-xs font-semibold text-slate-600 mb-1">
                📦 매핑되지 않은 품목 (견적서 변환에서 등록 필요) ({mn.items.length}건)
              </div>
              <div className="text-xs bg-rose-50/60 border border-rose-200 rounded p-2 max-h-32 overflow-y-auto">
                {mn.items.join(', ')}
              </div>
            </div>
          )}
          {mn.prices.length > 0 && (
            <div>
              <div className="text-xs font-semibold text-slate-600 mb-1">
                💰 거래처별 단가에 추가 필요 ({mn.prices.length}건)
              </div>
              <div className="text-xs bg-rose-50/60 border border-rose-200 rounded p-2 max-h-40 overflow-y-auto">
                {mn.prices.map((pr, i) => (
                  <div key={i} className="py-0.5">
                    · <span className="font-medium">{pr.partner_name}</span> × {pr.item_name}
                    <span className="text-slate-500 ml-1">({pr.dates.join(', ')})</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function RoutingFailureBanner({
  groupId, groupName, depts, onRerunConvert,
}: {
  groupId: number | null
  groupName: string | null
  depts: UnmatchedDept[]
  onRerunConvert?: () => void
}) {
  const push = useToast((s) => s.push)
  const totalLines = depts.reduce((s, d) => s + d.line_count, 0)
  const [children, setChildren] = useState<Partner[]>([])
  // dept_code → 선택된 partner_id
  const [picks, setPicks] = useState<Record<string, number | ''>>({})
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!groupId) return
    api<Partner[]>(`/api/partners?group_id=${groupId}`)
      .then(setChildren)
      .catch(() => {})
  }, [groupId])

  const readyCount = depts.filter((d) => picks[d.dept_code || '']).length

  async function saveAll() {
    if (!groupId || readyCount === 0) return
    const mappings = depts
      .filter((d) => picks[d.dept_code || ''])
      .map((d) => ({ dept_code: d.dept_code, partner_id: picks[d.dept_code || ''] }))
    setBusy(true)
    try {
      const res = await api<{ created: number; updated: number }>(
        '/api/partner-groups/dept-mappings-bulk',
        { method: 'POST', body: { mappings } },
      )
      push(`부서 매핑 ${res.created + res.updated}건 저장 (신규 ${res.created} · 갱신 ${res.updated})`, 'success')
      if (onRerunConvert) {
        setTimeout(() => onRerunConvert(), 200)
      }
    } catch (e) {
      push(`매핑 저장 실패: ${e instanceof Error ? e.message : e}`, 'error', 6000)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="rounded-xl bg-rose-50 border border-rose-300 mb-4 p-4">
      <div className="text-sm font-bold text-rose-900 mb-1">
        ⚠ 출고전표를 만들 수 없습니다 — 부서 라우팅 실패
      </div>
      <div className="text-sm text-rose-800/90 leading-relaxed mb-3">
        발주서는 정상 파싱됐지만 (<b className="tabular-nums">{totalLines}</b>행),
        상위 거래처 <b>"{groupName ?? '?'}"</b> 아래 자식 매출처로 라우팅할 <b>부서 매핑</b>이 없습니다.
        아래에서 각 부서를 자식 매출처에 연결하고 저장하면 자동으로 재변환됩니다.
      </div>

      <div className="rounded bg-white border border-rose-200 p-2.5">
        <div className="text-[11px] font-semibold text-rose-800 mb-2">
          발견된 부서 코드 ({depts.length}종) → 자식 매출처 선택
        </div>
        <table className="w-full text-xs">
          <thead className="text-[10px] uppercase text-slate-500 tracking-wide">
            <tr>
              <th className="text-left py-1 pr-2 font-medium">부서 코드</th>
              <th className="text-right py-1 pr-2 font-medium w-14">라인수</th>
              <th className="text-left py-1 font-medium">→ 매출처 선택</th>
            </tr>
          </thead>
          <tbody>
            {depts.map((d, i) => {
              const code = d.dept_code || ''
              return (
                <tr key={i} className="border-t border-slate-100">
                  <td className="py-1.5 pr-2 font-mono text-slate-800 break-all">
                    {code || '(부서 정보 없음)'}
                  </td>
                  <td className="py-1.5 pr-2 text-right tabular-nums text-slate-500">{d.line_count}</td>
                  <td className="py-1.5">
                    <select
                      value={picks[code] ?? ''}
                      disabled={!code}
                      onChange={(e) => setPicks((cur) => ({
                        ...cur,
                        [code]: e.target.value === '' ? '' : Number(e.target.value),
                      }))}
                      className="w-full px-2 py-1 border border-slate-300 rounded text-xs bg-white focus:border-rose-500 focus:ring-2 focus:ring-rose-100 outline-none"
                    >
                      <option value="">— 선택 —</option>
                      {children.map((p) => (
                        <option key={p.id} value={p.id}>{p.code} {p.name}</option>
                      ))}
                    </select>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-3 flex items-center gap-2">
        <span className="text-xs text-slate-600">
          매핑 대상: <b className="text-rose-800 tabular-nums">{readyCount}</b> / {depts.length}건
        </span>
        <div className="flex-1"/>
        <button
          onClick={saveAll}
          disabled={busy || readyCount === 0}
          className={cn(
            'text-sm font-semibold rounded-lg px-4 py-2 inline-flex items-center gap-1.5 focus:ring-2 focus:ring-rose-300 focus:outline-none',
            (busy || readyCount === 0)
              ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
              : 'bg-rose-600 hover:bg-rose-700 text-white',
          )}
        >
          {busy ? <Loader2 className="animate-spin w-4 h-4"/> : <Save className="w-4 h-4"/>}
          매핑 저장 {onRerunConvert ? '+ 재변환' : ''}
        </button>
        {onRerunConvert && (
          <button
            onClick={onRerunConvert}
            className="text-xs font-semibold text-slate-600 hover:text-slate-900 px-3 py-2 rounded-lg border border-slate-300 hover:bg-slate-50 inline-flex items-center gap-1"
          >
            <RotateCcw className="w-3.5 h-3.5"/> 다시 변환
          </button>
        )}
      </div>
    </div>
  )
}

function UnmatchedDeptsAlert({ depts }: { depts: UnmatchedDept[] }) {
  return (
    <div className="mx-4 mt-3 rounded-lg bg-white border border-rose-300 p-3">
      <div className="text-xs font-semibold text-rose-800 mb-2">
        ⚠ 매핑되지 않은 부서 ({depts.length}종, 라인 {depts.reduce((s, d) => s + d.line_count, 0)}건 손실)
      </div>
      <div className="text-xs bg-rose-50/60 border border-rose-200 rounded p-2 max-h-40 overflow-y-auto">
        {depts.map((d, i) => (
          <div key={i} className="py-0.5">
            · <span className="font-medium">{d.dept_code || '(부서 정보 없음)'}</span>
            <span className="text-slate-500 ml-1">→ {d.line_count}건 스킵됨</span>
          </div>
        ))}
      </div>
      <div className="text-[11px] text-slate-500 mt-2">
        상위 거래처 소속 파트너에 부서 코드 매핑 필요. 관리자에게 문의하거나 DB에 직접 추가.
      </div>
    </div>
  )
}

// ─── 출고전표 후보 리스트 (Section + SimpleRow) ────────────────────

function OrderSection({ p }: { p: ReviewProps }) {
  // 일괄 출고일자 — 대부분 파일별 하나의 날짜 (같은 발주서 = 같은 출고일).
  // 모든 order 가 같은 날짜면 그걸 표시, 다르면 첫 order 날짜.
  const dates = Array.from(new Set(p.orderRows.map((r) => r.order_date)))
  const bulkDate = dates[0] ?? ''
  const mixedDates = dates.length > 1
  function applyBulkDate(newDate: string) {
    if (!newDate) return
    p.setOrderRows((cur) => cur.map((r) => ({ ...r, order_date: newDate })))
  }

  return (
    <Section title="출고전표" rows={p.orderRows}
             onToggleAll={(v) => p.setOrderRows((cur) => cur.map((r) => ({ ...r, include: v })))}>
      {p.orderRows.length === 0 ? (
        <Empty>새로 등록할 출고전표가 없습니다.</Empty>
      ) : (
        <>
          {/* 일괄 출고일자 편집 */}
          <div className="px-4 py-2 bg-blue-50/50 border-t border-blue-100 flex items-center gap-2">
            <label className="text-xs font-semibold text-slate-600">일괄 출고일자</label>
            <input
              type="date" value={bulkDate}
              onChange={(e) => applyBulkDate(e.target.value)}
              className="px-2 py-1 border border-slate-300 rounded text-xs tabular-nums bg-white focus:border-emerald-500 focus:ring-1 focus:ring-emerald-100 outline-none"
            />
            <span className="text-[11px] text-slate-500">
              → 모든 출고전표 {p.orderRows.length}건에 일괄 적용
            </span>
            {mixedDates && (
              <span className="text-[11px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-semibold" title={`서로 다른 날짜 ${dates.length}종: ${dates.join(', ')}`}>
                ⚠ 다중 날짜 {dates.length}
              </span>
            )}
          </div>

          <div className="max-h-72 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase text-slate-500 tracking-wide bg-blue-100/40">
                <tr>
                  <th className="px-3 py-1.5 w-10"></th>
                  <th className="text-left px-3 py-1.5">거래처</th>
                  <th className="text-left px-3 py-1.5 w-28">출고일자</th>
                  <th className="text-right px-3 py-1.5 w-16">라인</th>
                  <th className="text-center px-3 py-1.5 w-32">품목</th>
                  <th className="text-center px-3 py-1.5 w-32">단가</th>
                  <th className="text-right px-3 py-1.5 w-28">합계 (DB 기준)</th>
                </tr>
              </thead>
            <tbody>
              {p.orderRows.map((r, i) => {
                const total = r.lines.reduce(
                  (sum, l) => sum + Number(l.qty) * Number(l.db_price ?? l.unit_price),
                  0,
                )
                const missingPrice = r.missing_price_count ?? r.lines.filter((l) => l.db_price_missing).length
                const diffLines = r.lines.filter((l) => l.price_diff)
                const diff = r.price_diff_count ?? diffLines.length
                const newItems = r.new_item_count ?? r.lines.filter((l) => l.is_new_item).length
                const newItemNames = r.new_item_names ?? []
                const willSkip = newItems > 0
                const won = (v: string | number | null | undefined) =>
                  v == null || v === '' ? '-' : Number(v).toLocaleString()
                return (
                  <Fragment key={i}>
                  <SimpleRow include={r.include}
                             onInclude={(v) => p.setOrderRows((cur) => cur.map((x, k) => k === i ? { ...x, include: v } : x))}
                             rowCls={willSkip ? 'bg-rose-50/40' : ''}>
                    <td className="px-3 py-1.5 text-xs">
                      {r.partner_name}
                      {r.is_new_partner && <Badge className="ml-1.5">신규</Badge>}
                      {r.note && (
                        <span className="ml-1.5 text-[10px] text-slate-400 tabular-nums">{r.note}</span>
                      )}
                    </td>
                    <td className="px-3 py-1.5 text-xs tabular-nums text-slate-600">
                      {r.order_date}
                    </td>
                    <td className="px-3 py-1.5 text-right text-xs tabular-nums">{r.lines.length}</td>
                    <td className="px-3 py-1.5 text-center text-[10px]">
                      {newItems > 0 ? (
                        <span
                          className="inline-block px-1.5 py-0.5 rounded bg-rose-100 text-rose-800 font-semibold cursor-help"
                          title={`품목관리에 없는 품목 ${newItems}건:\n${newItemNames.join(', ')}\n\n→ 이 주문은 등록되지 않습니다.\n② 신규 품목 섹션에서 먼저 추가하세요.`}
                        >
                          미등록 {newItems} ⚠
                        </span>
                      ) : (
                        <span className="inline-block px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 font-semibold" title="모든 품목 매칭됨">
                          매칭 {r.lines.length}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-1.5 text-center text-[10px]">
                      {missingPrice > 0 ? (
                        <span className="inline-block px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-semibold" title="거래처별 단가 미등록 (PO 값으로 fallback)">
                          미등록 {missingPrice}
                        </span>
                      ) : (
                        <span className="inline-block px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 font-semibold" title="모두 DB 단가 사용">
                          DB {r.lines.length}
                        </span>
                      )}
                      {diff > 0 && (
                        <span className="ml-1 inline-block px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-semibold" title="견적(DB) 단가로 등록됩니다 — 아래 상세 참고">
                          불일치 {diff}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{total.toLocaleString()}</td>
                  </SimpleRow>
                  {diffLines.length > 0 && (
                    <tr className="bg-amber-50/70 border-t border-amber-100/60">
                      <td></td>
                      <td colSpan={6} className="px-3 pb-2 pt-0.5">
                        <div className="text-[11px] text-amber-900">
                          <span className="font-semibold">단가 불일치 {diffLines.length}건</span>
                          <span className="text-amber-700/80"> — 견적(DB) 단가로 등록됩니다. (발주서 값은 참고용)</span>
                          <ul className="mt-1 space-y-0.5">
                            {diffLines.map((l, k) => (
                              <li key={k} className="tabular-nums flex flex-wrap items-baseline gap-x-2">
                                <span className="text-slate-700 font-medium">{l.matched_item_name || l.item_name}</span>
                                <span>견적 <b className="text-emerald-700">{won(l.db_price)}</b>원</span>
                                <span className="text-slate-400">/</span>
                                <span>발주 <b className="text-rose-600">{won(l.unit_price)}</b>원</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </td>
                    </tr>
                  )}
                  </Fragment>
                )
              })}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-blue-200 bg-blue-50/60 font-semibold">
                <td></td>
                <td className="px-3 py-2 text-xs text-slate-700" colSpan={5}>
                  선택된 출고전표 합계 ({p.orderRows.filter((r) => r.include).length}건)
                </td>
                <td className="px-3 py-2 text-right tabular-nums text-slate-900">
                  {p.orderRows
                    .filter((r) => r.include)
                    .reduce(
                      (sum, r) => sum + r.lines.reduce(
                        (s, l) => s + Number(l.qty) * Number(l.db_price ?? l.unit_price),
                        0,
                      ),
                      0,
                    )
                    .toLocaleString()}
                </td>
              </tr>
            </tfoot>
            </table>
          </div>
        </>
      )}
    </Section>
  )
}

function Section({
  title, badgeCls, rows, onToggleAll, children,
}: {
  title: string; badgeCls?: string
  rows: { include: boolean }[]
  onToggleAll: (v: boolean) => void
  children: React.ReactNode
}) {
  const allIn = rows.length > 0 && rows.every((r) => r.include)
  const anyIn = rows.some((r) => r.include)
  return (
    <div className="border-t border-blue-200/70 first:border-t-0">
      <div className="px-4 py-2 bg-blue-100/40 flex items-center gap-3">
        <input type="checkbox" checked={allIn} disabled={!rows.length}
               ref={(el) => { if (el) el.indeterminate = !allIn && anyIn }}
               onChange={(e) => onToggleAll(e.target.checked)}
               className="w-4 h-4 accent-blue-600"/>
        <span className={cn('text-sm font-semibold text-blue-900', badgeCls && 'flex items-center gap-2')}>
          {title}
        </span>
        <span className="text-xs text-blue-700/70 tabular-nums">
          {rows.filter((r) => r.include).length} / {rows.length}
        </span>
      </div>
      {children}
    </div>
  )
}

function SimpleRow({
  include, onInclude, children, rowCls = '',
}: { include: boolean; onInclude: (v: boolean) => void; children: React.ReactNode; rowCls?: string }) {
  return (
    <tr className={cn('border-t border-blue-100/40', include ? 'bg-white/60' : 'bg-slate-100/50 text-slate-400', rowCls)}>
      <td className="px-3 py-1.5 text-center w-10">
        <input type="checkbox" checked={include}
               onChange={(e) => onInclude(e.target.checked)}
               className="w-4 h-4 accent-blue-600"/>
      </td>
      {children}
    </tr>
  )
}

function Empty({ children }: { children: React.ReactNode }) {
  return <div className="px-4 py-3 text-sm text-blue-800/60 italic">{children}</div>
}
